import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  FAQ_JSON_LD,
  ORGANIZATION_JSON_LD,
  SOFTWARE_JSON_LD,
  WEBSITE_JSON_LD,
  resolveSeo,
  SITE_URL,
} from "@/lib/seo";

/**
 * Route-aware document head for SEO + social sharing + AI discovery.
 * Pass overrides for dynamic pages (solution/legal detail).
 */
export default function Seo({
  title,
  description,
  image,
  noindex = false,
  type,
} = {}) {
  const { pathname } = useLocation();
  const seo = resolveSeo({
    pathname,
    title,
    description,
    image,
    noindex,
  });
  const ogType = type || seo.type;

  return (
    <Helmet>
      <html lang="en" />
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      <meta
        name="robots"
        content={seo.noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"}
      />
      <meta name="googlebot" content={seo.noindex ? "noindex, nofollow" : "index, follow"} />
      <link rel="canonical" href={seo.url} />
      <link rel="alternate" type="text/plain" href={`${SITE_URL}/llms.txt`} title="llms.txt" />
      <link rel="alternate" type="text/plain" href={`${SITE_URL}/llms-full.txt`} title="llms-full.txt" />

      {/* Open Graph */}
      <meta property="og:site_name" content={seo.siteName} />
      <meta property="og:locale" content={seo.locale} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:image:secure_url" content={seo.image} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={seo.imageAlt} />

      {/* Twitter / X */}
      <meta name="twitter:card" content={seo.twitterCard} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <meta name="twitter:image:alt" content={seo.imageAlt} />

      {/* App / PWA-ish */}
      <meta name="application-name" content={seo.siteName} />
      <meta name="apple-mobile-web-app-title" content={seo.siteName} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="theme-color" content="#0F172A" />
      <meta name="msapplication-TileColor" content="#0F172A" />
      <meta name="format-detection" content="telephone=no" />

      <script type="application/ld+json">{JSON.stringify(ORGANIZATION_JSON_LD)}</script>
      <script type="application/ld+json">{JSON.stringify(WEBSITE_JSON_LD)}</script>
      <script type="application/ld+json">{JSON.stringify(SOFTWARE_JSON_LD)}</script>
      <script type="application/ld+json">{JSON.stringify(FAQ_JSON_LD)}</script>
    </Helmet>
  );
}
